import React, { useState, useEffect, useRef, useMemo } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import styles from './styles';

const inputStyles = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    color: 'black',
    height: '42px',
    padding: 0,
    margin: 0,
    boxSizing: 'border-box',
};

function createCustomInput(inputRefHolder) {
    return function CustomInput(props) {
        const { innerRef, ...inputProps } = props;

        const handleRef = (el) => {
            inputRefHolder.current = el;
            if (innerRef) {
                if (typeof innerRef === 'function') {
                    innerRef(el);
                } else {
                    innerRef.current = el;
                }
            }
        };

        return (
            <input
                ref={handleRef}
                {...inputProps}
                style={{
                    ...inputProps.style,
                    ...inputStyles,
                }}
            />
        );
    };
}

export default function GooglePlaceSearch({ value, onChange }) {
    const [inputValue, setInputValue] = useState('');
    const selectedLabelRef = useRef('');
    const inputRef = useRef(null);

    const CustomInputComponent = useMemo(
        () => createCustomInput(inputRef),
        []
    );

    useEffect(() => {
        const label = value?.label || '';
        setInputValue(label);
        selectedLabelRef.current = label;
    }, [value?.label]);

    const handleInputChange = (newValue, { action }) => {
        if (action === 'input-change') {
            setInputValue(newValue);
        } else if (action === 'set-value') {
            const restoredValue = selectedLabelRef.current;
            setInputValue(restoredValue);

            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.blur();
                    inputRef.current.focus();
                }
            }, 0);
        }
    };

    const handleChange = (selected) => {
        const label = selected?.label || '';
        selectedLabelRef.current = label;
        setInputValue(label);
        onChange(selected);
    };

    const handleClear = () => {
        selectedLabelRef.current = '';
        setInputValue('');
        onChange(null);
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <GooglePlacesAutocomplete
                apiKey={process.env.GOOGLE_PLACE_SEARCH_API_KEY}
                autocompletionRequest={{
                    types: ['geocode', 'establishment'],
                    componentRestrictions: {
                        country: ['us'],
                    }
                }}
                selectProps={{
                    value: null,
                    inputValue,
                    onInputChange: handleInputChange,
                    onChange: handleChange,
                    components: {
                        Input: CustomInputComponent,
                    },
                    styles: {
                        ...styles,
                        container: (p) => ({
                            ...styles.container(p),
                            width: '100%',
                        }),
                        control: (p) => ({
                            ...styles.control(p),
                            paddingRight: '36px',
                        }),
                        valueContainer: (p) => ({
                            ...p,
                            padding: '2px 8px',
                            display: 'flex',
                            flexWrap: 'nowrap',
                        }),
                        input: (p) => ({
                            ...p,
                            margin: 0,
                            padding: 0,
                            flex: '1 1 auto',
                            display: 'inline-grid',
                            gridArea: '1 / 1 / 2 / 3',
                            gridTemplateColumns: '0 minmax(min-content, 1fr)',
                            '& input': {
                                gridArea: '1 / 2',
                            },
                        }),
                    },
                    className: "google-please-autocomplete-input",
                    placeholder: 'Add location',
                    openMenuOnFocus: false,
                    openMenuOnClick: false,
                    noOptionsMessage: ({ inputValue }) => inputValue ? 'No options' : null,
                    blurInputOnSelect: false,
                }}
            />
            {inputValue && (
                <button
                    type="button"
                    onClick={handleClear}
                    style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: '1px solid #ccc',
                        borderRadius: '50%',
                        width: '22px',
                        height: '22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#999',
                        fontSize: '14px',
                        lineHeight: 1,
                        padding: 0,
                    }}
                    aria-label="Clear location"
                >
                    ×
                </button>
            )}
        </div>
    );
}
